import { useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "../../../../../../../assets/svg/close-icon";
import EmojiStaticIcon from "../../../../../../../assets/svg/emoji-static-icon";
import {
  SharedWith,
  SHARED_WITH_LABELS,
  SHARED_WITH_OPTIONS,
} from "../utils/constants";
import {
  EmojiItem,
  getPopularEmojiItems,
  searchEmojiItems,
} from "../../../../../../../utils/functions/emoji-extractor";
import { emojiList } from "../../../../../../../utils/constants/emojiList";
import { visibleScrollbar } from "../../../../../../../utils/functions/get-visible-scrollbar";

interface CreateViewFormProps {
  formTitle?: string;
  viewName: string;
  viewEmoji: string;
  sharedWith: SharedWith | "";
  onViewNameChange: (name: string) => void;
  onEmojiChange: (emoji: string) => void;
  onSharedWithChange: (value: SharedWith) => void;
  scopeError?: string;
  onClose: () => void;
  onNext: () => void;
  isEditMode?: boolean;
}

export const CreateViewForm = ({
  formTitle = "Create view",
  viewName,
  viewEmoji,
  sharedWith,
  onViewNameChange,
  onEmojiChange,
  onSharedWithChange,
  scopeError,
  onClose,
  onNext,
  isEditMode = false,
}: CreateViewFormProps) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState("");
  const [emojiCategory, setEmojiCategory] = useState("");

  const emojiPickerRef = useRef<HTMLDivElement>(null);

  const filteredEmojis = useMemo(() => {
    let emojis: EmojiItem[] = [];
    if (emojiSearch?.trim()) {
      emojis = searchEmojiItems(emojiList, emojiSearch);
    } else if (emojiCategory) {
      emojis = emojiList?.filter((e) => e?.category === emojiCategory);
    } else {
      emojis = getPopularEmojiItems(emojiList, 100);
    }
    return emojis?.slice(0, 100);
  }, [emojiSearch, emojiCategory]);

  const emojiCategoryTabs = useMemo(() => {
    const categories = [...new Set(emojiList?.map((e) => e?.category))]?.filter(
      (c) => c !== "Component"
    );
    return categories?.map((c) => ({ name: c, label: c }));
  }, []);

  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        emojiPickerRef?.current &&
        !emojiPickerRef?.current?.contains(e?.target as Node)
      ) {
        setShowEmojiPicker(false);
        setEmojiSearch("");
        setEmojiCategory("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEmojiPicker]);

  return (
    <div className="flex flex-col h-full bg-pure-white dark:bg-pure-white-dk overflow-hidden">
      <div className="flex items-center justify-between px-[18px] py-3">
        <span className="text-base font-medium text-heading-text dark:text-heading-text-dk">
          {formTitle}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="w-4 h-4 bg-gray-100-text dark:bg-tag-gray-dk rounded-full flex items-center justify-center hover:scale-110 transition-transform"
          aria-label="Close"
        >
          <CloseIcon className="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-[18px]">
        <div className="flex flex-col gap-8 pt-9">
          <span className="text-lg font-normal text-verba-indigo dark:text-verba-indigo-dk">
            Enter view details
          </span>

          <div className="flex flex-col gap-1.5 w-[264px]">
            <label className="text-md font-normal text-heading-text dark:text-heading-text-dk">
              Name
            </label>
            <div className="flex gap-1 items-start">
              <input
                type="text"
                value={viewName}
                onChange={(e) => onViewNameChange(e?.target?.value)}
                placeholder="View name"
                disabled={isEditMode}
                className="flex-1 h-9 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg px-2 text-md text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk outline-none focus:border-heading-text dark:focus:border-heading-text-dk transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="relative" ref={emojiPickerRef}>
                <button
                  type="button"
                  disabled={isEditMode}
                  onClick={() => setShowEmojiPicker((prev) => !prev)}
                  className="w-9 h-9 shrink-0 bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg flex items-center justify-center hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors disabled:opacity-50 disabled:cursor-not-allowed! disabled:hover:bg-pure-white dark:disabled:hover:bg-pure-white-dk"
                >
                  {viewEmoji ? (
                    <span className="text-2xl leading-none">{viewEmoji}</span>
                  ) : (
                    <EmojiStaticIcon className="w-[18px] h-[18px]" />
                  )}
                </button>
                {showEmojiPicker && (
                  <div className="absolute top-10 -left-30 z-50 p-3 w-[288px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect backdrop-blur-[10px]">
                    <input
                      type="text"
                      placeholder="Search emojis"
                      value={emojiSearch}
                      onChange={(e) => setEmojiSearch(e?.target?.value)}
                      className="w-full px-3 py-[6px] mb-2 border border-border-gray-375 dark:border-gray-375-dk rounded-[6px] text-xs focus:outline-none text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk bg-transparent"
                    />

                    {!emojiSearch && (
                      <div className="mb-2 flex flex-wrap gap-1">
                        <button
                          onClick={() => setEmojiCategory("")}
                          className={`px-[6px] py-[2px] text-xxs rounded-[4px] cursor-pointer transition-colors ${
                            emojiCategory === ""
                              ? "bg-verba-blue dark:bg-verba-blue-dk text-fixed-white dark:text-fixed-white-dk"
                              : "bg-hover-effect dark:bg-hover-effect-dk text-dark-gray dark:text-dark-gray-dk hover:opacity-80"
                          }`}
                        >
                          Popular
                        </button>
                        {emojiCategoryTabs?.map((tab) => (
                          <button
                            key={tab?.name}
                            onClick={() => setEmojiCategory(tab?.name)}
                            className={`px-[6px] py-[2px] text-xxs rounded-[4px] cursor-pointer transition-colors ${
                              emojiCategory === tab?.name
                                ? "bg-verba-blue dark:bg-verba-blue-dk text-fixed-white dark:text-fixed-white-dk"
                                : "bg-hover-effect dark:bg-hover-effect-dk text-dark-gray dark:text-dark-gray-dk hover:opacity-80"
                            }`}
                          >
                            {tab?.label}
                          </button>
                        ))}
                      </div>
                    )}

                    <div
                      className={`grid grid-cols-8 gap-1 max-h-[200px] overflow-y-auto ${visibleScrollbar(
                        "[&::-webkit-scrollbar]:w-[6px]"
                      )}`}
                    >
                      {filteredEmojis?.map((emoji, index) => (
                        <button
                          key={`${emoji?.code?.join("-")}-${index}`}
                          onClick={() => {
                            onEmojiChange(emoji?.emoji);
                            setShowEmojiPicker(false);
                            setEmojiSearch("");
                            setEmojiCategory("");
                          }}
                          className="w-[30px] h-[30px] flex items-center justify-center text-2xl leading-none hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-[4px] cursor-pointer transition-colors"
                          title={emoji?.name}
                        >
                          {emoji?.emoji}
                        </button>
                      ))}
                    </div>

                    {filteredEmojis?.length === 0 && (
                      <div className="text-center text-md text-heading-text dark:text-heading-text-dk py-4">
                        No emojis found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
              Shared with
            </span>
            <div className="flex flex-col gap-1.5">
              {SHARED_WITH_OPTIONS?.map((option) => (
                <label
                  key={option}
                  className={`w-max flex items-center gap-[7px] ${isEditMode ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                >
                  <input
                    type="radio"
                    name="sharedWith"
                    value={option}
                    checked={sharedWith === option}
                    onChange={() => onSharedWithChange(option)}
                    disabled={isEditMode}
                    className="appearance-none w-3 h-3 rounded-full border transition-colors shrink-0 border-gray-placeholder dark:border-gray-placeholder-dk checked:border-verba-blue dark:checked:border-verba-blue-dk checked:bg-verba-blue dark:checked:bg-verba-blue-dk cursor-pointer disabled:cursor-not-allowed [box-shadow:inset_0_0_0_2px_var(--color-pure-white)] dark:[box-shadow:inset_0_0_0_2px_var(--color-pure-white-dk)]"
                  />
                  <span className="text-md font-normal text-verba-indigo dark:text-verba-indigo-dk">
                    {SHARED_WITH_LABELS[option]}
                  </span>
                </label>
              ))}
              {scopeError && (
                <span className="text-xxs text-red-500 mt-1">{scopeError}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-[18px] py-[18px] backdrop-blur-[20px]">
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={onClose}
            className="h-[26px] w-[100px] flex items-center justify-center border border-border-gray dark:border-border-gray-dk rounded-md text-md font-semibold text-heading-text dark:text-heading-text-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onNext}
            disabled={!viewName?.trim()}
            className="h-[26px] px-12 flex items-center justify-center bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk rounded-md text-md font-semibold text-pure-white dark:text-pure-white-dk disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
          >
            {isEditMode ? "Edit View" : "Save View"}
          </button>
        </div>
      </div>
    </div>
  );
};
